/**
 * Token管理工具
 * 负责token的存储、获取、清除等操作
 */
import { createLogger } from '@/utils/logger';
import { createStorage } from '@/utils/storage';

const logger = createLogger('Token');
const storage = createStorage('Token');

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRE_TIME_KEY = 'token_expire_time';
const AUTH_USER_ID_KEY = 'auth_user_id';

/**
 * 存储访问令牌
 */
export function setAccessToken(token: string): void {
  storage.set(ACCESS_TOKEN_KEY, token);
}

/**
 * 获取访问令牌
 */
export function getAccessToken(): string | null {
  return storage.get(ACCESS_TOKEN_KEY);
}

/**
 * 存储刷新令牌
 */
export function setRefreshToken(token: string): void {
  storage.set(REFRESH_TOKEN_KEY, token);
}

/**
 * 获取刷新令牌
 */
export function getRefreshToken(): string | null {
  return storage.get(REFRESH_TOKEN_KEY);
}

/**
 * Token有效期限制（秒）
 */
const MAX_TOKEN_EXPIRE_TIME = 7200; // 2小时（兜底上限，实际以后端返回为准）

// ─── 闲置超时 ────────────────────────────────────────────────────────────────

const LAST_ACTIVE_TIME_KEY = 'last_active_time';

/**
 * 闲置超时阈值：2小时。
 * 规则：从用户最后一次业务请求算起，超过此时间未操作则强制重登。
 * 不受 token 续期影响——token 刷新本身不算"用户操作"。
 */
export const IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2小时

/**
 * 更新用户最后活跃时间（每次业务请求发出时调用，token 刷新请求除外）。
 */
export function updateLastActiveTime(): void {
  // Called on every request; log: false to avoid log spam
  storage.set(LAST_ACTIVE_TIME_KEY, Date.now().toString(), { log: false });
}

/**
 * 获取用户最后活跃时间戳，未记录时返回 null。
 */
export function getLastActiveTime(): number | null {
  const val = storage.get(LAST_ACTIVE_TIME_KEY);
  return val ? parseInt(val, 10) : null;
}

/**
 * 检查用户是否已闲置超时（超过 IDLE_TIMEOUT_MS 未发出任何业务请求）。
 * 没有记录时视为未超时（首次登录等场景）。
 */
export function isIdleTimeout(): boolean {
  const lastActive = getLastActiveTime();
  if (!lastActive) return false;
  return Date.now() - lastActive >= IDLE_TIMEOUT_MS;
}

/**
 * 清除最后活跃时间（登出时调用）。
 */
export function clearLastActiveTime(): void {
  storage.remove(LAST_ACTIVE_TIME_KEY);
}

/**
 * 存储token过期时间
 */
export function setTokenExpireTime(expiresIn: number): void {
  expiresIn = Math.min(expiresIn, MAX_TOKEN_EXPIRE_TIME);
  const expireTime = Date.now() + expiresIn * 1000;
  storage.set(TOKEN_EXPIRE_TIME_KEY, expireTime.toString());
}

/**
 * 获取token过期时间
 */
export function getTokenExpireTime(): number | null {
  const expireTime = storage.get(TOKEN_EXPIRE_TIME_KEY);
  return expireTime ? parseInt(expireTime, 10) : null;
}

/**
 * 检查token是否已过期
 */
export function isTokenExpired(): boolean {
  const expireTime = getTokenExpireTime();
  if (!expireTime) return false;

  return Date.now() >= expireTime;
}

/**
 * 检查是否应该尝试刷新 token。
 *
 * 刷新策略：
 * - token 有效期内（1h）：正常请求，不触发刷新
 * - token 过期后 1h 内（续期窗口）：允许刷新
 * - token 过期超过 1h：不允许刷新（应由闲置超时或后端 401 兜底）
 *
 * 注意：此函数只判断 token 时间窗口，不判断用户闲置状态。
 * 闲置超时检查（isIdleTimeout）应在调用此函数之前独立进行。
 */
export function shouldAutoRefresh(): boolean {
  const expireTime = getTokenExpireTime();
  if (!expireTime) return false;

  const now = Date.now();
  const timeLeft = expireTime - now;
  const oneHour = 60 * 60 * 1000;

  // 仅在 token 已过期且过期时间不超过 1 小时时允许刷新
  return timeLeft < 0 && timeLeft > -oneHour;
}

/**
 * 存储所有token信息
 */
export function setTokens(
  accessToken: string,
  refreshToken: string,
  expiresIn?: number,
): void {
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
  if (expiresIn) {
    setTokenExpireTime(expiresIn);
  }
}

/**
 * 清除所有token信息（包括闲置时间记录）
 */
export function clearTokens(): void {
  storage.removeMany([
    ACCESS_TOKEN_KEY,
    REFRESH_TOKEN_KEY,
    TOKEN_EXPIRE_TIME_KEY,
    AUTH_USER_ID_KEY,
    LAST_ACTIVE_TIME_KEY,
  ]);
}

/**
 * 存储当前登录用户 ID（仅在登录时调用，token 刷新时不更新）。
 * 用于跨 Tab 检测是否切换了用户。
 */
export function setAuthUserId(userId: string): void {
  storage.set(AUTH_USER_ID_KEY, userId);
}

export function getAuthUserId(): string | null {
  return storage.get(AUTH_USER_ID_KEY);
}

/**
 * 监听其他 Tab 的登录用户变化（同浏览器多账号登录保护）。
 *
 * 只监听 auth_user_id（仅登录时写入，token 刷新不会改变），
 * 因此同一用户的 token 刷新不会误触发。
 *
 * 同源的 localStorage 写操作会在 *其他* Tab 触发 StorageEvent，
 * 当前 Tab 自身的写操作不会触发，因此不会误判。
 */
export function onTokenChangedByOtherTab(
  callback: (reason: 'other_user_login' | 'cleared') => void,
): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key !== AUTH_USER_ID_KEY) return;

    if (e.newValue === null) {
      callback('cleared');
    } else if (e.oldValue && e.newValue !== e.oldValue) {
      callback('other_user_login');
    }
  };

  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

/**
 * 解析JWT Token（不验证签名，仅解析payload）
 */
export function parseJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => {
          return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
        })
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    logger.error('JWT解析失败', error);
    return null;
  }
}
